/**
 * StepScan.tsx — First security scan running page.
 *
 * Triggers the scan, shows animated progress, polls every 5s,
 * and shows results when done.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import API_BASE from '../../../config/api';
import RiskScore from '../../../components/RiskScore';

interface Finding { id: string; title: string; description: string; severity: string; }
interface ScanStatus {
  status: 'pending' | 'running' | 'completed' | 'failed';
  findings?: Finding[];
}

interface Props {
  sessionId: string;
  customerId: string;
  onNext: (riskScore: number, findingCount: number) => void;
}

const STAGES = [
  { emoji: '🔍', label: 'Connecting to your cloud…' },
  { emoji: '📊', label: 'Checking security settings…' },
  { emoji: '🤖', label: 'Analysing with AI…' },
  { emoji: '📋', label: 'Preparing your report…' },
];

export default function StepScan({ sessionId, customerId, onNext }: Props) {
  const navigate = useNavigate();
  const [scanId, setScanId]           = useState<string | null>(null);
  const [stage, setStage]             = useState(0);
  const [scanDone, setScanDone]       = useState(false);
  const [findings, setFindings]       = useState<Finding[]>([]);
  const [riskScore, setRiskScore]     = useState<number | null>(null);
  const [error, setError]             = useState('');
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Start the scan once on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.post(`${API_BASE}/onboarding/${sessionId}/start-scan`);
        setScanId(data.scan_id);
      } catch (err: any) {
        setError('Could not start the scan — you can retry from your dashboard.');
      }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line

  // Advance the stage indicator every 6 seconds
  useEffect(() => {
    const t = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 6000);
    return () => clearInterval(t);
  }, []);

  // Poll scan status every 5 seconds once we have a scan_id
  useEffect(() => {
    if (!scanId) return;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get<ScanStatus>(`${API_BASE}/v1/scans/${scanId}`);
        if (data.status === 'completed' || data.status === 'failed') {
          if (pollRef.current) clearInterval(pollRef.current);
          setStage(STAGES.length - 1);
          setScanDone(true);
          if (data.findings) setFindings(data.findings);

          // Fetch risk score for this customer
          try {
            const rd = await axios.get(`${API_BASE}/v1/dashboard/customers/${customerId}`);
            setRiskScore(rd.data.latest_score ?? 0);
          } catch { setRiskScore(0); }
        }
      } catch { /* ignore transient errors */ }
    }, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [scanId, customerId]);

  const topFindings = findings.slice(0, 3);
  const criticalCount = findings.filter((f) => f.severity === 'critical').length;
  const highCount     = findings.filter((f) => f.severity === 'high').length;
  const severityLabel = riskScore !== null
    ? riskScore <= 25 ? 'low' : riskScore <= 50 ? 'medium' : riskScore <= 75 ? 'high' : 'critical'
    : null;

  return (
    <Box maxWidth={560} mx="auto" textAlign="center" sx={{ position: 'relative' }}>
      <IconButton
        onClick={() => navigate(-1)}
        sx={{ position: 'absolute', top: 16, right: 16, color: 'text.secondary', '&:hover': { bgcolor: 'action.hover' } }}
      >
        <CloseIcon />
      </IconButton>
      <Typography variant="h5" fontWeight={700} mb={1}>
        Running your first security scan…
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={4}>
        This usually takes 1–3 minutes. We'll show your results right here.
      </Typography>

      {error && <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>{error}</Alert>}

      {/* Stage indicators */}
      <Stack spacing={1.5} sx={{ textAlign: 'left', mb: 4 }}>
        {STAGES.map((s, i) => {
          const done      = scanDone ? true : i < stage;
          const active    = !scanDone && i === stage;
          const pending   = !scanDone && i > stage;
          return (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5,
              opacity: pending ? 0.35 : 1, transition: 'opacity 0.4s' }}>
              <Typography sx={{ fontSize: 22, width: 32 }}>{s.emoji}</Typography>
              <Typography variant="body2" sx={{ flex: 1, fontWeight: active ? 600 : 400 }}>
                {s.label}
              </Typography>
              {done   && <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />}
              {active && <CircularProgress size={18} />}
            </Box>
          );
        })}
      </Stack>

      {/* Results */}
      {scanDone && riskScore !== null && (
        <Box>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="subtitle1" fontWeight={700} mb={2}>
            Scan complete — here's what we found
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <RiskScore score={riskScore} label={severityLabel} size="large" />
          </Box>
          <Typography variant="body1" fontWeight={600} mb={2}>
            We found {findings.length} security issue{findings.length !== 1 ? 's' : ''}
            {criticalCount > 0 && ` — ${criticalCount} critical`}
            {highCount > 0 && `, ${highCount} high priority`}
          </Typography>

          {topFindings.length > 0 && (
            <Stack spacing={1} sx={{ textAlign: 'left', mb: 3 }}>
              {topFindings.map((f) => (
                <Box key={f.id} sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.07)' }}>
                  <Typography variant="body2" fontWeight={600}>{f.title}</Typography>
                  <Typography variant="caption" color="text.secondary">{f.description?.slice(0, 120)}</Typography>
                </Box>
              ))}
            </Stack>
          )}

          <Button variant="contained" size="large" onClick={() => onNext(riskScore, findings.length)}
            sx={{ fontWeight: 700, px: 5 }}>
            View Full Report →
          </Button>
        </Box>
      )}

      {!scanDone && (
        <Typography variant="caption" color="text.disabled">
          You can close this window — the scan will keep running in the background.
        </Typography>
      )}
    </Box>
  );
}
